import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import React, { useMemo, useState } from 'react';

import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import {
  Box,
  Stack,
  Button,
  Select,
  MenuItem,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
} from '@mui/material';

import Iconify from 'src/components/iconify';

const RenderValue = ({ ruleId, cond, metadata, updateCondition, t }) => {
  const { field, value, id: condId } = cond;
  const commonSx = { minWidth: 200 };

  if (['createdAt', 'updatedAt'].includes(field)) {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <DatePicker
          label={t('popover.value')}
          value={value ? new Date(value) : null}
          onChange={(val) => updateCondition(ruleId, condId, { value: val })}
          slotProps={{ textField: { size: 'small', sx: commonSx } }}
        />
      </LocalizationProvider>
    );
  }

  if (metadata[field]) {
    return (
      <Autocomplete
        options={metadata[field]}
        size="small"
        value={value || null}
        onChange={(e, v) => updateCondition(ruleId, condId, { value: v })}
        renderInput={(params) => <TextField {...params} label={t('popover.value')} />}
        sx={commonSx}
      />
    );
  }

  return (
    <TextField
      size="small"
      label={t('popover.value')}
      value={value || ''}
      onChange={(e) => updateCondition(ruleId, condId, { value: e.target.value })}
      sx={commonSx}
    />
  );
};

RenderValue.propTypes = {
  ruleId: PropTypes.number.isRequired,
  cond: PropTypes.object.isRequired,
  metadata: PropTypes.object.isRequired,
  updateCondition: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
};

export default function AdvancedFilter({ ticketsWithStatus, setFilterByData, onClose }) {
  const { t } = useTranslation();

  const [rules, setRules] = useState([
    {
      id: Date.now(),
      operator: 'AND',
      conditions: [{ id: Date.now() + 1, field: 'id', operator: 'equals', value: '' }],
    },
  ]);
  const [rulesJoinOperator, setRulesJoinOperator] = useState('AND');

  const source = useMemo(
    () => (Array.isArray(ticketsWithStatus) ? ticketsWithStatus : []),
    [ticketsWithStatus]
  );

  const metadata = useMemo(
    () => ({
      department: [...new Set(source.map((t) => t?.department).filter(Boolean))],
      priority: [...new Set(source.map((t) => t?.priority).filter(Boolean))],
      status: [...new Set(source.map((t) => t?.status).filter(Boolean))],
      formName: [...new Set(source.map((t) => t?.formName).filter(Boolean))],
      agentName: [...new Set(source.map((t) => t?.agentName).filter(Boolean))],
      customerName: [...new Set(source.map((t) => t?.customerName).filter(Boolean))],
      attributeName: [
        ...new Set(
          source
            .flatMap((t) => (t?.tags || []).map((tg) => tg?.tag?.name))
            .filter(Boolean)
        ),
      ],
    }),
    [source]
  );

  const getOperatorsForField = (field) => {
    const ops = {
      id: ['equals', 'not_equals', 'greater_than', 'lesser_than'],
      createdAt: ['before', 'after'],
      updatedAt: ['before', 'after'],
      title: ['contains', 'equals', 'not_equals', 'starts_with'],
      attributeValue: ['contains', 'equals', 'not_equals', 'starts_with'],
      attributeName: ['contains'],
    };
    return ops[field] || ['equals', 'not_equals'];
  };

  const evaluateCondition = (ticket, cond) => {
    const { field, operator, value } = cond;
    if (!value && value !== 0) return true;
    const tVal = ticket[field];

    if (field === 'attributeName')
      return (ticket?.tags || []).some((tg) => tg?.tag?.name === value);
    if (field === 'attributeValue')
      return (ticket?.tags || []).some((tg) =>
        String(tg?.value || '')
          .toLowerCase()
          .includes(String(value).toLowerCase())
      );
    if (['createdAt', 'updatedAt'].includes(field)) {
      const tDate = new Date(tVal).getTime();
      const vDate = new Date(value).getTime();
      return operator === 'before' ? tDate < vDate : tDate > vDate;
    }

    const sTVal = String(tVal || '').toLowerCase();
    const sVVal = String(value || '').toLowerCase();

    switch (operator) {
      case 'equals':
        return sTVal === sVVal;
      case 'not_equals':
        return sTVal !== sVVal;
      case 'contains':
        return sTVal.includes(sVVal);
      case 'starts_with':
        return sTVal.startsWith(sVVal);
      case 'greater_than':
        return Number(tVal) > Number(value);
      case 'lesser_than':
        return Number(tVal) < Number(value);
      default:
        return true;
    }
  };

  const applyFilters = () => {
    const filtered = source.filter((ticket) => {
      const ruleResults = rules.map((rule) => {
        const condResults = rule.conditions.map((c) => evaluateCondition(ticket, c));
        return rule.operator === 'AND'
          ? condResults.every(Boolean)
          : condResults.some(Boolean);
      });
      return rulesJoinOperator === 'AND'
        ? ruleResults.every(Boolean)
        : ruleResults.some(Boolean);
    });
    setFilterByData(filtered);
    if (onClose) onClose();
  };

  const handleReset = () => {
    setRules([
      {
        id: Date.now(),
        operator: 'AND',
        conditions: [{ id: Date.now() + 1, field: 'id', operator: 'equals', value: '' }],
      },
    ]);
    setFilterByData(source);
    if (onClose) onClose();
  };

  const updateCondition = (ruleId, condId, data) => {
    setRules(
      rules.map((r) =>
        r.id === ruleId
          ? {
              ...r,
              conditions: r.conditions.map((c) =>
                c.id === condId ? { ...c, ...data } : c
              ),
            }
          : r
      )
    );
  };

  return (
    <Box sx={{ p: 2, bgcolor: '#F4F6F8', borderRadius: '24px', m: 1 }}>
      <Typography
        variant="subtitle2"
        sx={{
          mb: 2,
          fontWeight: 900,
          textTransform: 'uppercase',
          color: 'text.secondary',
        }}
      >
        {t('buttons.advancedFilter')}
      </Typography>

      {rules.map((rule, rIdx) => (
        <React.Fragment key={rule.id}>
          {rIdx > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: -1.5,
                mb: 1,
                zIndex: 2,
                position: 'relative',
              }}
            >
              <Select
                size="small"
                value={rulesJoinOperator}
                onChange={(e) => setRulesJoinOperator(e.target.value)}
                sx={{
                  bgcolor: 'background.paper',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '20px',
                  height: '26px',
                  fontSize: '0.7rem',
                  fontWeight: 900,
                  '& .MuiSelect-select': { py: 0, px: 2 },
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                }}
              >
                <MenuItem value="AND">AND</MenuItem>
                <MenuItem value="OR">OR</MenuItem>
              </Select>
            </Box>
          )}

          <Box
            sx={{
              p: 2,
              bgcolor: '#FFF',
              borderRadius: '16px',
              border: '1px solid rgba(0,0,0,0.08)',
              mb: rIdx === rules.length - 1 ? 0 : 2.5,
            }}
          >
            <Stack spacing={1.5}>
              {rule.conditions.map((c, cIdx) => (
                <Stack key={c.id} direction="row" spacing={1} alignItems="center">
                  {cIdx > 0 ? (
                    <Select
                      size="small"
                      value={rule.operator}
                      onChange={(e) =>
                        setRules(
                          rules.map((r) =>
                            r.id === rule.id ? { ...r, operator: e.target.value } : r
                          )
                        )
                      }
                      sx={{ minWidth: 85, height: '32px', fontWeight: 900 }}
                    >
                      <MenuItem value="AND">AND</MenuItem>
                      <MenuItem value="OR">OR</MenuItem>
                    </Select>
                  ) : (
                    <Box
                      sx={{
                        width: 85,
                        textAlign: 'center',
                        border: '1px solid rgba(0,0,0,0.1)',
                        borderRadius: '20px',
                        py: 0.4,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ fontWeight: 900, color: 'text.primary' }}
                      >
                        IF
                      </Typography>
                    </Box>
                  )}

                  <Select
                    size="small"
                    value={c.field}
                    onChange={(e) =>
                      updateCondition(rule.id, c.id, {
                        field: e.target.value,
                        value: '',
                        operator: getOperatorsForField(e.target.value)[0],
                      })
                    }
                    sx={{ minWidth: 150 }}
                  >
                    <MenuItem value="id">ID</MenuItem>
                    <MenuItem value="title">{t('ticket.title')}</MenuItem>
                    <MenuItem value="department">{t('ticket.department')}</MenuItem>
                    <MenuItem value="priority">{t('ticket.priority')}</MenuItem>
                    <MenuItem value="status">{t('ticket.status')}</MenuItem>
                    <MenuItem value="agentName">{t('ticket.agent')}</MenuItem>
                    <MenuItem value="customerName">{t('attributes.createdBy')}</MenuItem>
                    <MenuItem value="formName">{t('ticket.formName')}</MenuItem>
                    <MenuItem value="attributeName">
                      {t('attributes.attributeName')}
                    </MenuItem>
                    <MenuItem value="attributeValue">
                      {t('attributes.attributeValue')}
                    </MenuItem>
                    <MenuItem value="createdAt">{t('ticket.createdAt')}</MenuItem>
                    <MenuItem value="updatedAt">{t('ticket.updatedAt')}</MenuItem>
                  </Select>

                  <Select
                    size="small"
                    value={c.operator}
                    onChange={(e) =>
                      updateCondition(rule.id, c.id, { operator: e.target.value })
                    }
                    sx={{ minWidth: 120 }}
                  >
                    {getOperatorsForField(c.field).map((op) => (
                      <MenuItem key={op} value={op}>
                        {t(`filter.operators.${op}`)}
                      </MenuItem>
                    ))}
                  </Select>

                  <RenderValue
                    ruleId={rule.id}
                    cond={c}
                    metadata={metadata}
                    updateCondition={updateCondition}
                    t={t}
                  />

                  <IconButton
                    color="error"
                    size="small"
                    onClick={() =>
                      setRules(
                        rules
                          .map((r) =>
                            r.id === rule.id
                              ? {
                                  ...r,
                                  conditions: r.conditions.filter(
                                    (con) => con.id !== c.id
                                  ),
                                }
                              : r
                          )
                          .filter((r) => r.conditions.length > 0)
                      )
                    }
                  >
                    <Iconify icon="eva:trash-2-outline" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                startIcon={<Iconify icon="eva:plus-fill" />}
                onClick={() =>
                  setRules(
                    rules.map((r) =>
                      r.id === rule.id
                        ? {
                            ...r,
                            conditions: [
                              ...r.conditions,
                              {
                                id: Date.now(),
                                field: 'title',
                                operator: 'contains',
                                value: '',
                              },
                            ],
                          }
                        : r
                    )
                  )
                }
                sx={{ alignSelf: 'flex-start', color: 'primary.main', fontWeight: 900 }}
              >
                {t('filter.addCondition')}
              </Button>
            </Stack>
          </Box>
        </React.Fragment>
      ))}

      <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
        <Button
          variant="outlined"
          startIcon={<Iconify icon="eva:layers-fill" />}
          onClick={() =>
            setRules([
              ...rules,
              {
                id: Date.now(),
                operator: 'AND',
                conditions: [
                  { id: Date.now() + 1, field: 'id', operator: 'equals', value: '' },
                ],
              },
            ])
          }
          sx={{ borderRadius: '12px', fontWeight: 900 }}
        >
          {t('filter.addRule')}
        </Button>
        <Stack direction="row" spacing={2} alignItems="center">
          <Button
            startIcon={<Iconify icon="eva:refresh-fill" />}
            onClick={handleReset}
            color="inherit"
            sx={{ fontWeight: 900 }}
          >
            {t('buttons.reset')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Iconify icon="eva:checkmark-circle-2-fill" />}
            onClick={applyFilters}
            sx={{ borderRadius: '12px', px: 4, fontWeight: 900, bgcolor: 'primary.main' }}
          >
            {t('buttons.applyFilter')}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}

AdvancedFilter.propTypes = {
  ticketsWithStatus: PropTypes.array.isRequired,
  setFilterByData: PropTypes.func.isRequired,
  onClose: PropTypes.func,
};
